"use client"
import { useCallback, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import Input from "@/components/core/Input/Input"
import Button from "@/components/core/Button/Button"
import { selectAuthState } from "@/store/features/authSlice"
import { useSelector, useDispatch } from "react-redux"
import {
  sanitizeDOMString,
  sanitizeInnerHtml,
  validateField,
} from "@/utils/helper"
import { getConfig } from "@/constants/config"
import newsLetterBody from "@/components/Shared/NewsletterFooter/newsLetterBody"
import _get from "lodash/get"
import { validateRecaptcha } from "@/components/core/ReCaptcha/ReCaptcha"
import { apim } from "@/constants/api"
import { emailSubscribeEndPoint } from "@/constants/index"
import { getTranslations } from "@/components/Shared/NewsletterFooter/localization"
import { showToast } from "@/store/features/genericSlice"
import { useTranslation } from "next-i18next"
import DynamicImage from "@/components/Default/Image/v1/DynamicImage"
import { addNewsletterSignupAnalytics, subModalAnalytics } from "./analytics"

const DELAY = 10000 // 10 seconds
const COOLDOWN = 48 * 60 * 60 * 1000 // 48 hours
const CLEARENCE = 90 * 24 * 60 * 60 * 1000 // 90 Days
const STORAGE_KEY_LAST_COLSED_TIME = "newsletter_modal_last_closed_time"
const STORAGE_KEY_ALREADY_SUBCRIBED = "newsletter_modal_already_subcribed"
const FOCUSABLE_ELEMENTS_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
const DIALOG_IDS = {
  signupTitle: "newsletter-signup-title",
  signupDescription: "newsletter-signup-description",
  successTitle: "newsletter-signup-success-title",
  successDescription: "newsletter-signup-success-description",
  email: "newsletter-popup-email",
  emailError: "newsletter-popup-email-error",
  emailHint: "newsletter-popup-email-hint",
  formStatus: "newsletter-popup-form-status",
}

export default function NewsLetterPopUp(props) {
  const dispatch = useDispatch()
  const newLetterPopupInputref = useRef(null)
  const submitButton = useRef(null)
  const modalContentRef = useRef(null)
  const successModalContentRef = useRef(null)
  const previousFocusedElementRef = useRef(null)
  const newsletterTimerRef = useRef(null)
  const [validationError, setValidationError] = useState("")
  const [openNewsLetter, setOpenNewsLetter] = useState(false)
  const [hasShownNewsletter, setHasShownNewsletter] = useState(false)
  const [leadGenPageUrl, setLeadGenPageUrl] = useState(null)
  const [siteName, setSiteName] = useState("")
  const [language, setLanguage] = useState("en")
  const [isAlreadyExist, setIsAlreadyExist] = useState(false)
  const [isResetForm, setIsResetForm] = useState(false)
  const [successModel, setSuccessModel] = useState(false)
  const [staticTexts, setStaticTexts] = useState({})
  const [submissionStatus, setSubmissionStatus] = useState("")
  const { t } = useTranslation("common")
  const { innerClass, parentClass, data } = props
  const {
    submit,
    postSubmitDescription,
    imagePath,
    description,
    placeholder,
    title,
    imageVariationEnabled,
    postSubmitTitle,
  } = data || {}

  const [email, setEmail] = useState("")
  const {
    authModal: { show: isAuthOpen },
  } = useSelector(selectAuthState)
  const isModalOpen = openNewsLetter || successModel
  const emailLabel = placeholder || "Email address"
  const emailDescribedBy = [
    DIALOG_IDS.emailHint,
    validationError ? DIALOG_IDS.emailError : "",
    submissionStatus ? DIALOG_IDS.formStatus : "",
  ]
    .filter(Boolean)
    .join(" ")

  const getFocusableElements = useCallback(modalElement => {
    if (!modalElement) {
      return []
    }

    return [...modalElement.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR)].filter(
      element =>
        !element.hasAttribute("disabled") &&
        element.getAttribute("aria-hidden") !== "true"
    )
  }, [])

  const getActiveModalElement = useCallback(() => {
    if (openNewsLetter) {
      return modalContentRef.current
    }

    if (successModel) {
      return successModalContentRef.current
    }

    return null
  }, [openNewsLetter, successModel])

  const handleNewsLetterSignUp = async () => {
    setSubmissionStatus("")

    if (
      email === "" ||
      email == null ||
      email?.length === 0 ||
      email?.length > 60
    ) {
      setValidationError(staticTexts.enterEmail)
      newLetterPopupInputref?.current?.focus()
      return
    }
    if (email?.length > 0) {
      const isValid = await validateField("email", email)
      if (!isValid) {
        setValidationError(staticTexts.validEmail)
        newLetterPopupInputref?.current?.focus()
        return
      } else {
        setValidationError("")
        if (leadGenPageUrl) {
          window.location.href = `${leadGenPageUrl}?email=${email}`
          return
        } else {
          getSubscriptionData(email)
        }
      }
    }
  }

  const getSubscriptionData = email => {
    const body = newsLetterBody(siteName, email, language)
    validateRecaptcha()
      .then(res => {
        if (res.success) {
          apim
            .post(emailSubscribeEndPoint, body, {
              params: {
                brand: siteName,
              },
            })
            .then(response => {
              if (response && response.data) {
                const isSuccess = response.data.resultMessages ?? []
                if (isSuccess) {
                  // Code to handle success
                  setOpenNewsLetter(false)
                  setSuccessModel(true)
                  setIsResetForm(true)
                  setSubmissionStatus(staticTexts.submissionCompleteMsg || "")
                  localStorage.setItem(STORAGE_KEY_ALREADY_SUBCRIBED, true)
                  localStorage.setItem(
                    STORAGE_KEY_LAST_COLSED_TIME,
                    Date.now().toString()
                  )
                }
                setEmail("")
                addNewsletterSignupAnalytics(siteName, "success", "", false)
              } else {
                addNewsletterSignupAnalytics(
                  siteName,
                  "failure",
                  "news letter subscription api: error occured while fetching data from get url property",
                  false
                )
              }
            })
            .catch(err => {
              if (
                err.response?.data?.error === "Email address already subscribed"
              ) {
                setIsAlreadyExist(true)
                setValidationError(staticTexts.alreadySubscribed)
                setSubmissionStatus("")
                localStorage.setItem(STORAGE_KEY_ALREADY_SUBCRIBED, true)
                localStorage.setItem(
                  STORAGE_KEY_LAST_COLSED_TIME,
                  Date.now().toString()
                )
                addNewsletterSignupAnalytics(
                  siteName,
                  "failure",
                  staticTexts?.alreadySubscribed.toLowerCase(),
                  false
                )
              } else {
                //  HANDLE OTHER ERRORS
                setSubmissionStatus(staticTexts.genError)
                dispatch(
                  showToast({
                    message: staticTexts.genError,
                    isVisible: true,
                  })
                )
                addNewsletterSignupAnalytics(
                  siteName,
                  "failure",
                  staticTexts.genError.toLowerCase(),
                  false
                )
              }
            })
        } else {
          setSubmissionStatus(staticTexts.genError)
          dispatch(
            showToast({
              message: staticTexts.genError,
              isVisible: true,
            })
          )
          addNewsletterSignupAnalytics(
            siteName,
            "failure",
            staticTexts.genError.toLowerCase(),
            false
          )
        }
      })
      .catch(() => {
        setSubmissionStatus(staticTexts.genError)
        dispatch(
          showToast({
            message: staticTexts.genError,
            isVisible: true,
          })
        )
        addNewsletterSignupAnalytics(
          siteName,
          "failure",
          staticTexts.genError.toLowerCase(),
          false
        )
      })
  }

  const onClose = useCallback(() => {
    subModalAnalytics("close", "", false)
    setOpenNewsLetter(false)
    setSuccessModel(false)
    setSubmissionStatus("")
    localStorage.setItem(STORAGE_KEY_LAST_COLSED_TIME, Date.now().toString())
  }, [])

  const setInitalData = async () => {
    const config = await getConfig()
    setSiteName(_get(config, "general.siteName", ""))
    setLanguage(_get(config, "internationalization.language", "en"))
  }

  useEffect(() => {
    setStaticTexts(getTranslations(t))
    setInitalData()
  }, [t])

  useEffect(() => {
    const canShowNewsletter = () => {
      if (hasShownNewsletter || isAuthOpen) {
        return false
      }

      const lastClosed = localStorage.getItem(STORAGE_KEY_LAST_COLSED_TIME)
      const alreadySubcribed = localStorage.getItem(
        STORAGE_KEY_ALREADY_SUBCRIBED
      )

      if (alreadySubcribed && alreadySubcribed === "true") {
        // code to check storage clearence after 90 days
        const now = Date.now()
        const diff = now - parseInt(lastClosed, 10)
        if (CLEARENCE < diff) {
          localStorage.removeItem(STORAGE_KEY_ALREADY_SUBCRIBED)
          localStorage.removeItem(STORAGE_KEY_LAST_COLSED_TIME)
        }
        return false
      }

      // If modal was closed before, enforce 48-hour cooldown
      if (lastClosed) {
        const now = Date.now()
        const diff = now - parseInt(lastClosed, 10)

        if (diff < COOLDOWN) {
          return false
        }
      }
      return true
    }

    clearTimeout(newsletterTimerRef.current)
    newsletterTimerRef.current = null

    if (!canShowNewsletter()) {
      if (isAuthOpen) {
        setOpenNewsLetter(false)
      }
      return undefined
    }

    newsletterTimerRef.current = setTimeout(() => {
      setHasShownNewsletter(true)
      setOpenNewsLetter(true)
    }, DELAY)

    return () => {
      clearTimeout(newsletterTimerRef.current)
      newsletterTimerRef.current = null
    }
  }, [hasShownNewsletter, isAuthOpen])

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("scroll-lock", "modal-open")
    } else {
      document.body.classList.remove("scroll-lock", "modal-open")
    }

    return () => {
      document.body.classList.remove("scroll-lock", "modal-open")
    }
  }, [isModalOpen])

  useEffect(() => {
    if (isResetForm) setEmail("")
    if (isAlreadyExist) setValidationError(staticTexts?.alreadySubscribed)
  }, [isResetForm, isAlreadyExist])

  useEffect(() => {
    if (!openNewsLetter) {
      setValidationError("")
      setSubmissionStatus("")
      setIsAlreadyExist(false)
    }
  }, [openNewsLetter])

  const handleFormSubmit = e => {
    e.preventDefault()
    handleNewsLetterSignUp()
  }

  useEffect(() => {
    if (!isModalOpen) {
      if (previousFocusedElementRef.current instanceof HTMLElement) {
        previousFocusedElementRef.current.focus()
      }
      previousFocusedElementRef.current = null
      return undefined
    }

    if (!(previousFocusedElementRef.current instanceof HTMLElement)) {
      previousFocusedElementRef.current = document.activeElement
    }

    if (openNewsLetter) {
      newLetterPopupInputref?.current?.focus()
      return undefined
    }

    const activeModalElement = getActiveModalElement()
    const [firstFocusableElement] = getFocusableElements(activeModalElement)

    if (firstFocusableElement instanceof HTMLElement) {
      firstFocusableElement.focus()
    } else if (activeModalElement instanceof HTMLElement) {
      activeModalElement.focus()
    }

    return undefined
  }, [getActiveModalElement, getFocusableElements, isModalOpen])

  useEffect(() => {
    if (!isModalOpen) {
      return undefined
    }

    const handleKeyDown = e => {
      if (e.key === "Escape") {
        onClose()
        return
      }

      if (e.key !== "Tab") {
        return
      }

      const activeModalElement = getActiveModalElement()
      const focusableElements = getFocusableElements(activeModalElement)

      if (focusableElements.length === 0) {
        e.preventDefault()
        activeModalElement?.focus()
        return
      }

      const firstFocusableElement = focusableElements[0]
      const lastFocusableElement = focusableElements[focusableElements.length - 1]
      const activeElement = document.activeElement

      if (e.shiftKey && activeElement === firstFocusableElement) {
        e.preventDefault()
        lastFocusableElement.focus()
      } else if (!e.shiftKey && activeElement === lastFocusableElement) {
        e.preventDefault()
        firstFocusableElement.focus()
      } else if (!activeModalElement?.contains(activeElement)) {
        e.preventDefault()
        firstFocusableElement.focus()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [getActiveModalElement, getFocusableElements, isModalOpen, onClose])

  if (isAuthOpen) {
    return null
  }
  if (!data) {
    return null
  }
  if (!openNewsLetter) {
    if (successModel) {
      return (
        <div onClick={onClose} className={parentClass}>
          <div
            ref={successModalContentRef}
            onClick={e => e.stopPropagation()}
            className={`${innerClass} success-popup`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={DIALOG_IDS.successTitle}
            aria-describedby={DIALOG_IDS.successDescription}
            tabIndex={-1}
          >
            <button
              onClick={onClose}
              className="close-button"
              aria-label="Close newsletter signup modal"
              type="button"
            >
              <img
                src="/images/newsletter-close.svg"
                alt=""
                aria-hidden="true"
              />
            </button>
            <div className="newsletter-popup_content">
              <h1
                className="newsletter-popup-header succes-error"
                id={DIALOG_IDS.successTitle}
              >
                {sanitizeInnerHtml(postSubmitTitle)}
              </h1>
              <div
                className="newsletter-popup-message succes-error"
                id={DIALOG_IDS.successDescription}
                role="status"
                aria-live="polite"
              >
                {sanitizeInnerHtml(postSubmitDescription)}
              </div>
            </div>
          </div>
        </div>
      )
    } else {
      return null
    }
  } else {
    return (
      <div onClick={onClose} className={parentClass}>
        <div
          ref={modalContentRef}
          onClick={e => e.stopPropagation()}
          className={innerClass}
          role="dialog"
          aria-modal="true"
          aria-labelledby={DIALOG_IDS.signupTitle}
          aria-describedby={DIALOG_IDS.signupDescription}
          tabIndex={-1}
        >
          <button
            onClick={onClose}
            className="close-button"
            aria-label="Close newsletter signup modal"
            type="button"
          >
            <img
              src="/images/newsletter-close.svg"
              alt=""
              aria-hidden="true"
            />
          </button>
          <div className="newsletter-popup_box">
            {imageVariationEnabled ? (
              <div className="newsletter-popup_image">
                <DynamicImage
                  className="teaser-pop-up-image"
                  imgUrl={imagePath}
                  alt=""
                  aria-hidden="true"
                />
              </div>
            ) : null}
            <div className="newsletter-popup_content">
              <h1
                className="newsletter-popup-header"
                id={DIALOG_IDS.signupTitle}
              >
                {sanitizeInnerHtml(title)}
              </h1>
              <span id={DIALOG_IDS.signupDescription} className="sr-only">
                Newsletter signup form. Enter your email address to subscribe.
              </span>
              <span id={DIALOG_IDS.emailHint} className="sr-only">
                {emailLabel} is required.
              </span>
              <div
                id={DIALOG_IDS.formStatus}
                className="sr-only"
                aria-live="polite"
              >
                {sanitizeDOMString(submissionStatus)}
              </div>
              <form
                className="newsletter-popup-form"
                aria-label="Newsletter sign up"
                onSubmit={handleFormSubmit}
                noValidate
              >
                <Input
                  id={DIALOG_IDS.email}
                  ref={newLetterPopupInputref}
                  type="email"
                  value={email}
                  maxLength={60}
                  placeholder={placeholder}
                  label={emailLabel}
                  name="email"
                  autoComplete="email"
                  onChange={e => {
                    const { value } = e.target
                    setEmail(value)
                    if (validationError) {
                      setValidationError("")
                    }
                    if (submissionStatus) {
                      setSubmissionStatus("")
                    }
                  }}
                  showError={false}
                  errorMessage=""
                  onBlur={() => {}}
                  required
                  aria-invalid={validationError ? "true" : "false"}
                  aria-describedby={emailDescribedBy}
                  className={validationError ? "email-error":"email"}
                />
                {validationError && (
                  <div className="subsribe-error-container">
                    <span
                      id={DIALOG_IDS.emailError}
                      role="alert"
                      aria-live="assertive"
                      data-testid="email-error"
                    >
                      {sanitizeDOMString(validationError)}
                    </span>
                  </div>
                )}
                <Button
                  ref={submitButton}
                  customClass="newsletter-popup-signup gbh-data-layer"
                  type="signup"
                  label={submit}
                  ariaLabel={submit || "Sign up for newsletter"}
                  tag="button"
                  htmlType="submit"
                  flexible
                />
              </form>
              <div className="newsletter-popup-policy">
                {sanitizeInnerHtml(description)}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

NewsLetterPopUp.protoTypes = {
  innerClass: PropTypes.string,
  parentClass: PropTypes.string,
  data: PropTypes.object,
}
